import { useCallback, useRef } from 'react'
import { createId, titleFromPrompt } from '@/features/chat/chatIds'
import { createThread } from '@/services/chat/threadsApi'

/**
 * Give the home composer a chat to upload into.
 *
 * `/v2` uploads are chat-scoped, but on home no conversation exists when a file
 * is picked — it is otherwise minted at send time. So one is minted here and
 * registered in the rail.
 *
 * It is deliberately **not** selected. Selecting flips `showChatThread`, which
 * swaps the home composer for the thread one — a different React subtree — and
 * the in-flight upload would go down with the unmounted component. Send selects
 * it instead, once the composer is expected to be replaced anyway.
 *
 * The in-flight promise is cached, not its result: the file picker starts every
 * upload synchronously, so two files selected together would otherwise both see
 * an empty cache and mint two chats.
 */
export function useHomeChatIdStart({ m }) {
  const pending = useRef(/** @type {Promise<object> | null} */ (null))

  return useCallback(
    (fileName) => {
      if (pending.current) return pending.current
      const id = createId()
      const title = titleFromPrompt(fileName || 'Attachment')
      m.setThreads((prev) => [{ id, title, projectId: m.activeProjectId ?? null }, ...prev])
      pending.current = createThread({ id, title, projectId: m.activeProjectId })
        // A failed create is not a failed upload: /v2 creates the conversation
        // itself, just titled "New chat". Send repairs the title.
        .catch(() => null)
        .then(() => ({ id, provisional: true, projectId: m.activeProjectId ?? null }))
      return pending.current
    },
    [m],
  )
}
